import { MindMapNode, YouTubeVideoEntry } from '../types';

const CLIENT_ID = '111155376912-96c48rso744m0qi4gq3kr05992986h2m.apps.googleusercontent.com';
const SCOPE = 'https://www.googleapis.com/auth/youtube https://www.googleapis.com/auth/youtube.force-ssl';
const CONNECTED_KEY = 'sb_youtube_connected';

let tokenClient: any = null;
let accessToken: string | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let onConnectCallback: ((connected: boolean) => void) | null = null;
const subscribers = new Set<(connected: boolean) => void>();

export const subscribeYouTube = (fn: (connected: boolean) => void) => {
    subscribers.add(fn);
    if (accessToken) fn(true); // fire immediately if already connected
    return () => subscribers.delete(fn);
};

const notify = (connected: boolean) => subscribers.forEach(fn => fn(connected));

// Google access tokens last ~1h. Once expired (or rejected with 401) the app must show
// "not connected" so the user can reconnect — never silently re-open the OAuth popup ourselves.
// CONNECTED_KEY is kept so the next page load still tries a silent re-auth.
export const markYouTubeDisconnected = () => {
    accessToken = null;
    if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
    onConnectCallback?.(false);
    notify(false);
};

export const initYouTube = (onConnect: (connected: boolean) => void) => {
    onConnectCallback = onConnect;

    const trySetup = (): boolean => {
        const google = (window as any).google;
        if (!google?.accounts?.oauth2) return false;

        tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: CLIENT_ID,
            scope: SCOPE,
            callback: (resp: any) => {
                if (resp.error) {
                    // Silent refresh failed — clear flag quietly, don't flash "disconnected" to UI
                    localStorage.removeItem(CONNECTED_KEY);
                    return;
                }
                accessToken = resp.access_token;
                if (expiryTimer) clearTimeout(expiryTimer);
                const lifetimeMs = (Number(resp.expires_in) || 3600) * 1000;
                expiryTimer = setTimeout(markYouTubeDisconnected, Math.max(0, lifetimeMs - 30_000));
                localStorage.setItem(CONNECTED_KEY, '1');
                onConnect(true);
                notify(true);
            },
            error_callback: () => {
                // Silent refresh failed — clear flag quietly
                localStorage.removeItem(CONNECTED_KEY);
            },
        });

        if (localStorage.getItem(CONNECTED_KEY)) {
            tokenClient.requestAccessToken({ prompt: '' });
        }
        return true;
    };

    if (trySetup()) return;
    // The GIS script tag loads with async/defer, so it can still be mid-load when this runs on
    // mount — without this, tokenClient stays null forever and connectYouTube() silently no-ops
    // on every click. Poll briefly (10s) rather than assume it's ready.
    let attempts = 0;
    const interval = setInterval(() => {
        attempts++;
        if (trySetup() || attempts >= 50) clearInterval(interval);
    }, 200);
};

export const connectYouTube = () => {
    if (!tokenClient) return;
    // Always show account picker when user explicitly connects — avoid silent re-auth loop
    tokenClient.requestAccessToken({ prompt: 'select_account' });
};

export const disconnectYouTube = () => {
    const google = (window as any).google;
    if (accessToken) {
        google?.accounts?.oauth2?.revoke(accessToken, () => {});
        accessToken = null;
        if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
    }
    localStorage.removeItem(CONNECTED_KEY);
    if (onConnectCallback) onConnectCallback(false);
};

export const isYouTubeConnected = () => !!accessToken;

export const getYouTubeChannel = async (): Promise<{ name: string; thumbnail: string } | null> => {
    if (!accessToken) return null;
    try {
        const res = await fetch(
            'https://www.googleapis.com/youtube/v3/channels?part=snippet&mine=true',
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        const ch = data.items?.[0]?.snippet;
        if (!ch) return null;
        return { name: ch.title, thumbnail: ch.thumbnails?.default?.url ?? '' };
    } catch { return null; }
};

export interface YouTubeUploadOptions {
    file: File;
    title: string;
    description: string;
    tags?: string[];
    thumbnailBase64?: string | null;
    thumbnailMime?: string | null;
    visibility?: 'public' | 'unlisted' | 'private';
    madeForKids?: boolean; // required "made for kids" declaration — omitted only if the caller has none
    onProgress?: (percent: number) => void;
}

export interface YouTubeUploadResult {
    videoId: string;
    videoUrl: string;
    title: string;
    /** Whether the custom thumbnail was accepted by YouTube during the upload. */
    thumbnailSet?: boolean;
}

/**
 * Tags as YouTube accepts them. One bad tag makes YouTube reject the whole upload ("invalid video
 * keywords"): it forbids < and >, and limits all tags together to 500 characters (a tag with a
 * space counts two more, for the quotes it is stored with). Note tags are written by hand or by
 * the AI, so anything can be in them — a long sentence is not a tag and is dropped.
 */
export const sanitizeYouTubeTags = (tags: string[] | undefined): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    let total = 0;
    for (const raw of tags ?? []) {
        const tag = String(raw ?? '').replace(/[#<>"]/g, '').replace(/\s+/g, ' ').trim();
        if (!tag || tag.length > 60 || tag.split(' ').length > 5) continue;
        const key = tag.toLowerCase();
        if (seen.has(key)) continue;
        const cost = tag.length + (tag.includes(' ') ? 2 : 0) + (out.length ? 1 : 0);
        if (total + cost > 480) break;
        seen.add(key);
        out.push(tag);
        total += cost;
    }
    return out;
};

export const uploadToYouTube = async (opts: YouTubeUploadOptions): Promise<YouTubeUploadResult> => {
    if (!accessToken) throw new Error('not_connected');

    const metadata = {
        snippet: {
            title: opts.title.slice(0, 100),
            description: opts.description.slice(0, 5000),
            tags: sanitizeYouTubeTags(opts.tags),
            categoryId: '27', // Education
        },
        status: {
            privacyStatus: opts.visibility ?? 'unlisted',
            ...(opts.madeForKids !== undefined ? { selfDeclaredMadeForKids: opts.madeForKids } : {}),
        },
    };

    // 1. Initiate resumable upload
    const initRes = await fetch(
        'https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status',
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                'X-Upload-Content-Type': opts.file.type || 'video/*',
                'X-Upload-Content-Length': String(opts.file.size),
            },
            body: JSON.stringify(metadata),
        }
    );

    if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}));
        if (initRes.status === 401) throw new Error('not_connected');
        throw new Error(err?.error?.message ?? 'Failed to initiate upload');
    }

    const uploadUrl = initRes.headers.get('Location');
    if (!uploadUrl) throw new Error('No upload URL returned');

    // 2. Upload file in chunks (5MB each)
    const CHUNK = 5 * 1024 * 1024;
    let offset = 0;
    let videoId = '';

    while (offset < opts.file.size) {
        const chunk = opts.file.slice(offset, offset + CHUNK);
        const end = Math.min(offset + CHUNK, opts.file.size) - 1;

        const res = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Range': `bytes ${offset}-${end}/${opts.file.size}`,
                'Content-Type': opts.file.type || 'video/*',
            },
            body: chunk,
        });

        if (res.status === 200 || res.status === 201) {
            const data = await res.json();
            videoId = data.id;
            break;
        } else if (res.status === 308) {
            // Resume incomplete
            const range = res.headers.get('Range');
            offset = range ? parseInt(range.split('-')[1]) + 1 : offset + CHUNK;
            opts.onProgress?.(Math.round((offset / opts.file.size) * 90));
        } else {
            const err = await res.json().catch(() => ({}));
            throw new Error(err?.error?.message ?? `Upload failed at ${offset}`);
        }
    }

    if (!videoId) throw new Error('Upload completed but no video ID returned');
    opts.onProgress?.(95);

    // 3. Upload thumbnail if provided. Never fails the upload, but the outcome is checked and
    // retried: this call used to be fire-and-forget, so a rejected thumbnail (YouTube still
    // ingesting the new video, or an image over the 2 MB limit) silently left the video with
    // YouTube's auto-picked frame.
    let thumbnailSet = false;
    if (opts.thumbnailBase64 && opts.thumbnailMime) {
        try {
            const base64Data = opts.thumbnailBase64.replace(/^data:[^;]+;base64,/, '');
            const binary = atob(base64Data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            let thumbBlob = new Blob([bytes], { type: opts.thumbnailMime });
            if (thumbBlob.size > 2 * 1024 * 1024) thumbBlob = await shrinkThumbnail(thumbBlob);

            for (let attempt = 0; attempt < 4 && !thumbnailSet; attempt++) {
                if (attempt > 0) await new Promise(r => setTimeout(r, attempt * 5000));
                const thumbRes = await fetch(
                    `https://www.googleapis.com/upload/youtube/v3/thumbnails/set?videoId=${videoId}&uploadType=media`,
                    {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': thumbBlob.type },
                        body: thumbBlob,
                    }
                );
                if (thumbRes.ok) thumbnailSet = true;
                else console.error('[youtube] thumbnail rejected', thumbRes.status, await thumbRes.text().catch(() => ''));
            }
        } catch (e) {
            console.error('[youtube] thumbnail upload failed', e); // non-fatal
        }
    }

    opts.onProgress?.(100);

    return {
        videoId,
        videoUrl: `https://www.youtube.com/watch?v=${videoId}`,
        title: opts.title,
        thumbnailSet,
    };
};

// YouTube rejects thumbnails over 2 MB — re-encode as JPEG at the same 16:9 size.
const shrinkThumbnail = async (blob: Blob): Promise<Blob> => {
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = 1280; canvas.height = 720;
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, 1280, 720);
    return await new Promise<Blob>(res => canvas.toBlob(b => res(b || blob), 'image/jpeg', 0.9));
};

export interface YouTubeUpdateOptions {
    title?: string;
    description?: string;
    privacyStatus?: 'private' | 'public' | 'unlisted';
    publishAt?: string; // ISO 8601 — makes it scheduled
    madeForKids?: boolean; // "Is this video made for kids?" declaration (status.selfDeclaredMadeForKids)
    thumbnailBase64?: string | null;
    thumbnailMime?: string | null;
}

export const updateYouTubeVideo = async (videoId: string, opts: YouTubeUpdateOptions): Promise<void> => {
    if (!accessToken) throw new Error('not_connected');

    // Build update body — YouTube requires snippet.title + snippet.categoryId when updating snippet
    const body: any = { id: videoId };
    const parts: string[] = [];

    if (opts.title !== undefined || opts.description !== undefined) {
        // Fetch existing snippet first so we don't accidentally wipe tags/defaultLanguage
        const existing = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        ).then(r => r.json()).catch(() => null);
        const existingSnippet = existing?.items?.[0]?.snippet ?? {};
        body.snippet = {
            ...existingSnippet,
            title: (opts.title ?? existingSnippet.title ?? '').slice(0, 100) || 'StudyBoard Video',
            description: (opts.description ?? existingSnippet.description ?? '').slice(0, 5000),
            categoryId: existingSnippet.categoryId || '27',
        };
        parts.push('snippet');
    }

    if (opts.privacyStatus || opts.publishAt || opts.madeForKids !== undefined) {
        // videos.update overwrites every mutable field of the "status" part, so start from the current
        // values — otherwise changing privacy would silently clear the kids declaration (and vice versa).
        const existingStatus = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=status&id=${videoId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        ).then(r => r.json()).catch(() => null);
        const st = existingStatus?.items?.[0]?.status ?? {};
        body.status = {
            privacyStatus: st.privacyStatus,
            embeddable: st.embeddable,
            license: st.license,
            publicStatsViewable: st.publicStatsViewable,
            selfDeclaredMadeForKids: st.selfDeclaredMadeForKids,
            ...(st.publishAt ? { publishAt: st.publishAt } : {}),
        };
        if (opts.privacyStatus) body.status.privacyStatus = opts.privacyStatus;
        if (opts.publishAt) { body.status.publishAt = opts.publishAt; body.status.privacyStatus = 'private'; }
        if (opts.madeForKids !== undefined) body.status.selfDeclaredMadeForKids = opts.madeForKids;
        parts.push('status');
    }

    if (parts.length > 0) {
        const res = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=${parts.join(',')}`,
            {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }
        );
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            if (res.status === 401) throw new Error('not_connected');
            const reason = err?.error?.errors?.[0]?.reason;
            const msg = err?.error?.message ?? `Update failed (HTTP ${res.status})`;
            throw new Error(reason ? `${msg} [${reason}]` : msg);
        }
    }

    // Update thumbnail if provided. A rejected upload (e.g. unverified channel, quota) must surface —
    // callers save "this is what's on YouTube" state after a successful push, so it can't be silent.
    if (opts.thumbnailBase64 && opts.thumbnailMime) {
        const raw = opts.thumbnailBase64.replace(/^data:[^;]+;base64,/, '');
        const bytes = Uint8Array.from(atob(raw), c => c.charCodeAt(0));
        const thumbRes = await fetch(
            `https://www.googleapis.com/upload/youtube/v3/thumbnails/set?videoId=${videoId}&uploadType=media`,
            {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': opts.thumbnailMime },
                body: new Blob([bytes], { type: opts.thumbnailMime }),
            }
        );
        if (!thumbRes.ok) throw await youtubeApiError(thumbRes);
    }
};

export interface YouTubePlaylist {
    id: string;
    title: string;
    itemCount: number;
    thumbnail?: string;
}

// Human-readable reason from a failed YouTube API response, e.g. "quota exceeded [quotaExceeded]".
const youtubeApiError = async (res: Response): Promise<Error> => {
    if (res.status === 401) { markYouTubeDisconnected(); return new Error('not_connected'); }
    const err = await res.json().catch(() => ({}));
    const reason = err?.error?.errors?.[0]?.reason;
    const msg = err?.error?.message ?? `HTTP ${res.status}`;
    return new Error(reason ? `${msg} [${reason}]` : msg);
};

// Throws (with YouTube's own reason, or 'not_connected') when the list can't be read — an empty
// result must only ever mean "the channel really has no playlists".
export const fetchYouTubePlaylists = async (): Promise<YouTubePlaylist[]> => {
    if (!accessToken) throw new Error('not_connected');
    const all: YouTubePlaylist[] = [];
    let pageToken = '';
    // Follow every page (capped) — a single page of 50 silently hid newer playlists.
    for (let page = 0; page < 10; page++) {
        const res = await fetch(
            `https://www.googleapis.com/youtube/v3/playlists?part=snippet,contentDetails&mine=true&maxResults=50${pageToken ? `&pageToken=${pageToken}` : ''}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!res.ok) throw await youtubeApiError(res);
        const data = await res.json();
        (data.items || []).forEach((item: any) => all.push({
            id: item.id,
            title: item.snippet?.title || '(untitled)',
            itemCount: item.contentDetails?.itemCount ?? 0,
            thumbnail: item.snippet?.thumbnails?.default?.url,
        }));
        if (!data.nextPageToken) break;
        pageToken = data.nextPageToken;
    }
    return all;
};

// videoId -> names of the playlists containing it, for every video in every playlist of the channel.
// One pass over each playlist (1 quota unit per 50 items) is far cheaper than asking per video.
export const fetchPlaylistMembershipMap = async (): Promise<{ map: Record<string, string[]>; playlists: YouTubePlaylist[] }> => {
    if (!accessToken) throw new Error('not_connected');
    const playlists = await fetchYouTubePlaylists();
    const map: Record<string, string[]> = {};
    let next = 0;
    let failure = null as Error | null;
    const worker = async () => {
        while (next < playlists.length && !failure) {
            const pl = playlists[next++];
            let pageToken = '';
            for (let page = 0; page < 20; page++) {
                const res = await fetch(
                    `https://www.googleapis.com/youtube/v3/playlistItems?part=contentDetails&playlistId=${encodeURIComponent(pl.id)}&maxResults=50${pageToken ? `&pageToken=${pageToken}` : ''}`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                if (!res.ok) { failure = await youtubeApiError(res); return; }
                const data = await res.json();
                for (const it of (data.items || [])) {
                    const vid = it.contentDetails?.videoId;
                    if (!vid) continue;
                    (map[vid] ||= []).includes(pl.title) || map[vid].push(pl.title);
                }
                if (!data.nextPageToken) break;
                pageToken = data.nextPageToken;
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(4, playlists.length) }, worker));
    if (failure) throw failure;
    return { map, playlists };
};

// Throws an Error carrying YouTube's own reason on failure (callers decide how to show it).
export const createYouTubePlaylist = async (title: string, description = ''): Promise<YouTubePlaylist> => {
    if (!accessToken) throw new Error('not_connected');
    const res = await fetch(
        'https://www.googleapis.com/youtube/v3/playlists?part=snippet,status,contentDetails',
        {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ snippet: { title: title.slice(0, 150), description }, status: { privacyStatus: 'private' } }),
        }
    );
    if (!res.ok) throw await youtubeApiError(res);
    const item = await res.json();
    return { id: item.id, title: item.snippet?.title || title, itemCount: 0, thumbnail: undefined };
};

export const addVideoToPlaylist = async (playlistId: string, videoId: string): Promise<void> => {
    if (!accessToken) throw new Error('not_connected');
    // A playlist that was created moments ago often isn't visible to playlistItems.insert yet —
    // YouTube answers 409 / 5xx "SERVICE_UNAVAILABLE ... operation was aborted". It resolves on its
    // own within seconds, so retry with a growing pause before giving up.
    const delays = [1500, 3000, 5000, 8000];
    for (let attempt = 0; ; attempt++) {
        const res = await fetch(
            'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet',
            {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ snippet: { playlistId, resourceId: { kind: 'youtube#video', videoId } } }),
            }
        );
        if (res.ok) return;
        const body = await res.clone().json().catch(() => ({}));
        const reason: string = body?.error?.errors?.[0]?.reason ?? '';
        const transient = res.status === 409 || res.status >= 500 || /SERVICE_UNAVAILABLE|backendError/i.test(reason);
        if (!transient || attempt >= delays.length) throw await youtubeApiError(res);
        await new Promise(r => setTimeout(r, delays[attempt]));
    }
};

// Parse SRT/VTT caption file → plain text (strips timestamps and indices)
const parseCaptionToText = (raw: string): string => {
    return raw
        .split('\n')
        .filter(line => {
            const t = line.trim();
            if (!t) return false;
            if (/^\d+$/.test(t)) return false; // SRT index
            if (/^\d{2}:\d{2}/.test(t)) return false; // timestamp line
            if (t.startsWith('WEBVTT')) return false;
            return true;
        })
        .map(l => l.trim())
        .join(' ')
        .replace(/<[^>]+>/g, '') // strip VTT tags like <c>
        .replace(/\s+/g, ' ')
        .trim();
};

/**
 * The same captions as parseCaptionToText, but keeping when things were said: one line per
 * ~15 seconds of speech, each starting with its [m:ss] mark. Used to derive description chapters.
 */
export const parseCaptionToTimedText = (raw: string): { timed: string; durationSecs: number } => {
    const cues: { start: number; end: number; text: string }[] = [];
    const toSecs = (h: string, m: string, s: string) => (+h) * 3600 + (+m) * 60 + (+s);
    for (const block of raw.replace(/\r/g, '').split(/\n\s*\n/)) {
        const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
        const at = lines.findIndex(l => /^\d{1,2}:\d{2}:\d{2}[.,]\d+\s*-->/.test(l));
        if (at === -1) continue;
        const m = lines[at].match(/^(\d{1,2}):(\d{2}):(\d{2})[.,]\d+\s*-->\s*(\d{1,2}):(\d{2}):(\d{2})/);
        if (!m) continue;
        const text = lines.slice(at + 1).join(' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        if (text) cues.push({ start: toSecs(m[1], m[2], m[3]), end: toSecs(m[4], m[5], m[6]), text });
    }
    const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
    const out: string[] = [];
    let lineStart = -1;
    let buf: string[] = [];
    const flush = () => { if (buf.length) out.push(`[${fmt(lineStart)}] ${buf.join(' ')}`); buf = []; };
    for (const c of cues) {
        if (lineStart === -1 || c.start - lineStart >= 15) { flush(); lineStart = c.start; }
        buf.push(c.text);
    }
    flush();
    return { timed: out.join('\n'), durationSecs: cues.length ? cues[cues.length - 1].end : 0 };
};

export const fetchYouTubeTranscript = async (videoId: string): Promise<{ text: string; timed: string } | { error: string }> => {
    if (!accessToken) return { error: 'Not connected to YouTube' };
    try {
        // 1. List caption tracks
        const listRes = await fetch(
            `https://www.googleapis.com/youtube/v3/captions?part=snippet&videoId=${videoId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (listRes.status === 401) { markYouTubeDisconnected(); return { error: 'auth_expired' }; }
        if (!listRes.ok) {
            const errData = await listRes.json().catch(() => ({}));
            return { error: `Captions list failed (${listRes.status}): ${errData?.error?.message || listRes.statusText}` };
        }
        const listData = await listRes.json();
        const tracks: any[] = listData.items || [];
        if (tracks.length === 0) return { error: 'no_captions' };

        // Prefer ASR (auto-generated) track, fallback to first available
        const asr = tracks.find((t: any) => t.snippet?.trackKind === 'asr') || tracks[0];
        if (!asr) return { error: 'no_captions' };

        // 2. Download caption content as SRT
        const dlRes = await fetch(
            `https://www.googleapis.com/youtube/v3/captions/${asr.id}?tfmt=srt`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (dlRes.status === 401) { markYouTubeDisconnected(); return { error: 'auth_expired' }; }
        if (!dlRes.ok) {
            const errData = await dlRes.json().catch(() => ({}));
            return { error: `Caption download failed (${dlRes.status}): ${errData?.error?.message || dlRes.statusText}` };
        }
        const raw = await dlRes.text();
        const text = parseCaptionToText(raw);
        if (!text) return { error: 'no_captions' };
        return { text, timed: parseCaptionToTimedText(raw).timed };
    } catch (e: any) { return { error: e.message || 'Unknown error' }; }
};

export interface MyYouTubeVideo {
    id: string;
    title: string;
    description: string;
    thumbnail?: string;
    publishedAt: string;
    privacyStatus: string;
}

export const fetchMyYouTubeVideos = async (maxResults = 50): Promise<MyYouTubeVideo[]> => {
    if (!accessToken) return [];
    try {
        // 1. Get uploads playlist ID from channel
        const chRes = await fetch(
            'https://www.googleapis.com/youtube/v3/channels?part=contentDetails&mine=true',
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const chData = await chRes.json();
        const uploadsPlaylistId = chData.items?.[0]?.contentDetails?.relatedPlaylists?.uploads;
        if (!uploadsPlaylistId) return [];

        // 2. List videos from uploads playlist
        const plRes = await fetch(
            `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${uploadsPlaylistId}&maxResults=${maxResults}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const plData = await plRes.json();
        const items: any[] = plData.items || [];
        const videoIds = items.map((it: any) => it.snippet?.resourceId?.videoId).filter(Boolean);
        if (videoIds.length === 0) return [];

        // 3. Fetch snippet + status (description, privacy)
        const vRes = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=snippet,status&id=${videoIds.join(',')}&maxResults=${maxResults}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const vData = await vRes.json();
        return (vData.items || []).map((v: any) => ({
            id: v.id,
            title: v.snippet?.title || '',
            description: v.snippet?.description || '',
            thumbnail: v.snippet?.thumbnails?.medium?.url || v.snippet?.thumbnails?.default?.url,
            publishedAt: v.snippet?.publishedAt || '',
            privacyStatus: v.status?.privacyStatus || 'private',
        }));
    } catch { return []; }
};

export const fetchYouTubeAudience = async (videoId: string): Promise<'kids' | 'notkids' | 'unset' | null> => {
    if (!accessToken) return null;
    try {
        const res = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=status&id=${videoId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (res.status === 401) { markYouTubeDisconnected(); return null; }
        const st = (await res.json()).items?.[0]?.status;
        if (!st) return null;
        if (st.selfDeclaredMadeForKids === true) return 'kids';
        if (st.selfDeclaredMadeForKids === false) return 'notkids';
        return 'unset';
    } catch { return null; }
};

export const fetchYouTubeVideoSnippet = async (videoId: string): Promise<{ title: string; description: string } | null> => {
    if (!accessToken) return null;
    try {
        const res = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (res.status === 401) { markYouTubeDisconnected(); return null; }
        const data = await res.json();
        const sn = data.items?.[0]?.snippet;
        return sn ? { title: sn.title || '', description: sn.description || '' } : null;
    } catch { return null; }
};

// Permanently deletes the video from the user's YouTube channel (cannot be undone). A video that is
// already gone (404) counts as deleted so the caller can still clean up its own records.
export const deleteYouTubeVideo = async (videoId: string): Promise<void> => {
    if (!accessToken) throw new Error('not_connected');
    const res = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?id=${encodeURIComponent(videoId)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (res.ok || res.status === 404) return;
    throw await youtubeApiError(res);
};

export const getYouTubeVideoStatus = async (videoId: string): Promise<'private' | 'scheduled' | 'public' | null> => {
    if (!accessToken) return null;
    try {
        const res = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?part=status&id=${videoId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        const status = data.items?.[0]?.status;
        if (!status) return null;
        if (status.publishAt) return 'scheduled';
        return status.privacyStatus === 'public' ? 'public' : 'private';
    } catch { return null; }
};

// ── Multi-video-per-note helpers ────────────────────────────────────────────
// A note's first video lives in the legacy singular fields ("slot 0"); youtubeVideos[]
// only ever holds the 2nd, 3rd, ... videos. No migration needed for existing notes.

export const getVideosForNode = (node: MindMapNode): YouTubeVideoEntry[] => {
    const legacy: YouTubeVideoEntry[] = node.youtubeVideoId ? [{
        id: node.youtubeVideoId,
        youtubeVideoId: node.youtubeVideoId,
        youtubeStatus: node.youtubeStatus || 'private',
        youtubeTitle: node.youtubeTitle,
        youtubeDescription: node.youtubeDescription,
        youtubeUploadedAt: node.youtubeUploadedAt,
        youtubeScheduledAt: node.youtubeScheduledAt,
        videoUrl: node.videoUrl,
        videoTitle: node.videoTitle,
        videoDuration: node.videoDuration,
        videoSegments: node.videoSegments,
        videoClipFocus: node.videoClipFocus,
        videoStoragePath: node.videoStoragePath,
        postCheckScore: node.postCheckScore,
        postCheckRecommendation: node.postCheckRecommendation,
        postCheckStrengths: node.postCheckStrengths,
        postCheckPasses: node.postCheckPasses,
        postCheckFindings: node.postCheckFindings,
        postCheckSummary: node.postCheckSummary,
        postCheckCheckedAt: node.postCheckCheckedAt,
        youtubeTranscript: node.youtubeTranscript,
        youtubeTimedTranscript: node.youtubeTimedTranscript,
        showOnGithub: node.showOnGithub,
        thumbnailSettings: node.thumbnailSettings,
        thumbnailPushedAt: node.thumbnailPushedAt,
        youtubeEditedAt: node.youtubeEditedAt,
        thumbnailDrafts: node.thumbnailDrafts,
        thumbnailStudio: node.thumbnailStudio,
        exportJobId: node.exportJobId,
        clipOfJobId: node.clipOfJobId,
    }] : [];
    // YouTube's own "unlisted" (or any unexpected value saved earlier) isn't a status the app models —
    // treat it as private so status lookups never come back undefined and crash the list.
    const known = ['private', 'scheduled', 'public'];
    return [...legacy, ...(node.youtubeVideos || [])].map(v =>
        v.youtubeStatus && !known.includes(v.youtubeStatus) ? { ...v, youtubeStatus: 'private' as const } : v);
};

// Decides slot-0-overwrite vs array-append for a newly uploaded/linked video.
// What a patch built below was meant to do — one video added, or some fields of one video changed.
// A patch for a later video carries the note's WHOLE youtubeVideos array, taken from whatever copy
// of the note the caller held. Written as is, a copy that predates another upload silently removes
// that upload's video from the note. safeUpdateDoc (services/videoPatchWrite.ts) uses this intent to
// redo the change on the note as it is on the server at write time.
export type VideoPatchIntent = { kind: 'append'; entry: YouTubeVideoEntry } | { kind: 'update'; videoId: string; fields: Partial<YouTubeVideoEntry> };
export const videoPatchIntent = new WeakMap<object, VideoPatchIntent>();

export const buildVideoAppendPatch = (
    node: Pick<MindMapNode, 'youtubeVideoId' | 'youtubeVideos'>,
    newVideo: Omit<YouTubeVideoEntry, 'id'>,
): Record<string, unknown> => {
    const entry: YouTubeVideoEntry = { id: newVideo.youtubeVideoId, ...newVideo };
    let patch: Record<string, unknown>;
    if (!node.youtubeVideoId) {
        const { id, ...legacyFields } = entry;
        patch = legacyFields; // slot 0 free — same shape as today's overwrite
    } else {
        patch = { youtubeVideos: [...(node.youtubeVideos || []), entry] };
    }
    videoPatchIntent.set(patch, { kind: 'append', entry });
    return patch;
};

// Decides flat-field-update vs array-element-update for editing an existing video.
export const buildVideoUpdatePatch = (
    node: Pick<MindMapNode, 'youtubeVideoId' | 'youtubeVideos'>,
    videoId: string,
    fields: Partial<YouTubeVideoEntry>,
): Record<string, unknown> => {
    const patch: Record<string, unknown> = node.youtubeVideoId === videoId
        ? { ...fields } // slot 0 — flat update
        : { youtubeVideos: (node.youtubeVideos || []).map(v => v.youtubeVideoId === videoId ? { ...v, ...fields } : v) };
    videoPatchIntent.set(patch, { kind: 'update', videoId, fields });
    return patch;
};

// Effective "show this video's link on the GitHub page" state, mirroring the server's
// collectGithubVideos: an explicit choice wins; otherwise only the first video (and only if it
// still has a link) counts, exactly as it did before the choice existed.
export const isVideoOnGithub = (node: MindMapNode, video: YouTubeVideoEntry): boolean =>
    video.showOnGithub ?? (node.youtubeVideoId === video.youtubeVideoId && !!node.videoUrl);

export type NoteVideoStatus = 'none' | 'private' | 'scheduled' | 'public' | 'mixed';

export const getNoteVideoStatus = (node: MindMapNode): NoteVideoStatus => {
    const videos = getVideosForNode(node);
    if (videos.length === 0) return 'none';
    const statuses = new Set(videos.map(v => v.youtubeStatus || 'private'));
    return statuses.size === 1 ? (statuses.values().next().value as NoteVideoStatus) : 'mixed';
};
